import React, { useContext, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { AuthProvider } from 'react-oidc-context';
import { Auth0Provider } from '@auth0/auth0-react';
import * as authUtils from './utils/authUtils';
import {
  storeAuthConfig,
  getStoredAuthConfig,
  hasStoredToken,
  storePostLoginRedirect,
} from './utils/authUtils';
import { configureAxios } from './utils/bbAxios';
import { SessionContext } from './SessionContext';
// Auth0-specific session management (consolidated hook)
import { useAuth0Session } from './hooks/useAuth0Session';
// OIDC-specific session management (consolidated hook)
import { useOIDCSession } from './hooks/useOIDCSession';
// GCP (Firebase) session management
import { useGCPSession, GCPSessionContext } from './hooks/useGCPSession';
// UI components
import { LoadingScreen, ErrorScreen, TimeoutDialog } from './components';
import { STORAGE_KEYS } from './sessionConfig';

/**
 * Auth0 Session Wrapper
 *
 * Uses the consolidated useAuth0Session hook with:
 * - Web Locks API for leader election (auto-cleanup on tab close)
 * - Activity tracking with localStorage as source of truth
 * - Token refresh with exponential backoff
 * - Terminal error detection (no retry on invalid_grant, etc.)
 * - Dialog state using absolute timestamps for accurate countdown
 * - Cross-tab synchronization via BroadcastChannel
 */
const Auth0SessionWrapper = React.memo(({ loginRoute, nonce, children }) => {
  // Use consolidated session hook
  const {
    isDialogOpen,
    expirationTimestamp,
    countdownFormatted,
    continueSession,
    logout,
  } = useAuth0Session({
    loginRoute,
    onSessionExpired: () => {
      console.log('[Auth0Session] Session expired');
    },
  });

  return (
    <>
      {children}
      <TimeoutDialog
        isOpen={isDialogOpen}
        expirationTimestamp={expirationTimestamp}
        timeRemainingFormatted={countdownFormatted}
        onContinue={continueSession}
        onLogout={logout}
        isRefreshing={false}
        nonce={nonce}
      />
    </>
  );
});

Auth0SessionWrapper.displayName = 'Auth0SessionWrapper';

/**
 * OIDC Session Wrapper
 *
 * Uses the new consolidated useOIDCSession hook with:
 * - Web Locks API for leader election (prevents multi-tab refresh race conditions)
 * - Activity tracking with localStorage as source of truth
 * - Token refresh with exponential backoff
 * - Terminal error detection (no retry on invalid_grant, etc.)
 * - Dialog state using absolute timestamps for accurate countdown
 * - Cross-tab synchronization via BroadcastChannel
 *
 * This replaces the old TokenRefreshWrapper which used 4 separate hooks:
 * - useActivityTracker
 * - useSessionTimeoutDialog
 * - useTokenRefresh
 * - useTokenSync
 */
const OIDCSessionWrapper = React.memo(({ loginRoute, nonce, children }) => {
  // Use consolidated OIDC session hook
  const {
    isDialogOpen,
    expirationTimestamp,
    countdownFormatted,
    continueSession,
    logout,
  } = useOIDCSession({
    loginRoute,
    onSessionExpired: () => {
      console.log('[OIDCSession] Session expired');
    },
  });

  return (
    <>
      {children}
      <TimeoutDialog
        isOpen={isDialogOpen}
        expirationTimestamp={expirationTimestamp}
        timeRemainingFormatted={countdownFormatted}
        onContinue={continueSession}
        onLogout={logout}
        isRefreshing={false}
        nonce={nonce}
      />
    </>
  );
});

OIDCSessionWrapper.displayName = 'OIDCSessionWrapper';

/**
 * GCP Session Wrapper
 *
 * Uses useGCPSession to manage Firebase Authentication:
 * - Initializes Firebase from the backend config
 * - Subscribes to onAuthStateChanged and stores the ID token
 * - Proactive token refresh before expiry
 * - Session timeout dialog (~2 min before ID token expiry)
 */
const GCPSessionWrapper = React.memo(({ loginRoute, nonce, children }) => {
  const { config } = useContext(SessionContext);
  const gcpSession = useGCPSession({
    config,
    loginRoute,
    onSessionExpired: () => {
      console.log('[GCPSession] Session expired');
    },
  });

  const { isDialogOpen, expirationTimestamp, countdownFormatted, continueSession, logout } =
    gcpSession;

  return (
    <GCPSessionContext.Provider value={gcpSession}>
      {children}
      <TimeoutDialog
        isOpen={isDialogOpen}
        expirationTimestamp={expirationTimestamp}
        timeRemainingFormatted={countdownFormatted}
        onContinue={continueSession}
        onLogout={logout}
        isRefreshing={false}
        nonce={nonce}
      />
    </GCPSessionContext.Provider>
  );
});

GCPSessionWrapper.displayName = 'GCPSessionWrapper';

const SessionManager = ({
  configEndpoint,
  homeRoute,
  loginRoute = '/bbweb/login',
  isSatellite = false,
  nonce,
  children,
  LoadingComponent = LoadingScreen,
  ErrorComponent = ErrorScreen,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);

  // Memoize context value - must be called before any conditional returns
  const sessionContextValue = useMemo(
    () => ({
      config,
      user,
      loginRoute,
      isSatellite,
      setUser,
      setConfig,
    }),
    [config, user, loginRoute, isSatellite]
  );

  useEffect(() => {
    (async () => {
      try {
        let fetchedConfig;

        if (isSatellite) {
          // Satellite app: read config from localStorage (stored by main app)
          fetchedConfig = getStoredAuthConfig();
          if (!fetchedConfig) {
            // No config available - redirect to main app login
            // The main app will store config after successful auth
            console.log(
              '[SessionManager] Satellite mode: No stored config, redirecting to login'
            );
            const url = new URL(window.location.href);
            url.searchParams.delete('redirect');
            const returnUrl = url.pathname + url.search;
            window.location.href = `${loginRoute}?redirect=${encodeURIComponent(returnUrl)}`;
            return;
          }

          // Also check if there's a valid OIDC/Auth0 token
          // Config may exist from a previous session, but token could be cleared
          if (!hasStoredToken()) {
            console.log(
              '[SessionManager] Satellite mode: Config exists but no token, redirecting to login'
            );
            const url = new URL(window.location.href);
            url.searchParams.delete('redirect');
            const returnUrl = url.pathname + url.search;
            window.location.href = `${loginRoute}?redirect=${encodeURIComponent(returnUrl)}`;
            return;
          }

          console.log('[SessionManager] Satellite mode: Using stored auth config');
        } else {
          // Main app: fetch config from API and store for satellites
          fetchedConfig = await authUtils.fetchConfig(configEndpoint);
          storeAuthConfig(fetchedConfig);
        }

        setConfig(fetchedConfig);
        configureAxios(loginRoute);
      } catch (err) {
        console.error('Error loading session configuration:', err);
        setError(err);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [configEndpoint, loginRoute, isSatellite]);

  if (isLoading) {
    return <LoadingComponent />;
  }

  if (error) {
    return <ErrorComponent message={error.message} error={error} />;
  }

  if (!config) {
    return null;
  }

  const { provider: authProvider } = config;

  // GCP uses Firebase directly — no OIDC provider config needed
  const authConfig =
    authProvider !== 'gcp'
      ? authUtils.initOIDCAuthProviderConfig(config, { redirectPath: homeRoute })
      : {};

  // Prevent "Invalid state" errors from stale auth callbacks (e.g., tab duplication).
  // When a tab is duplicated, the new tab inherits the callback URL (?code=...&state=...)
  // but the authorization code / state was already consumed by the original tab.
  // If a valid session already exists in localStorage, strip the stale params so the
  // auth provider loads from the existing session instead of attempting a doomed exchange.
  const currentUrl = new URL(window.location.href);
  if (
    currentUrl.searchParams.has('code') &&
    currentUrl.searchParams.has('state') &&
    hasStoredToken()
  ) {
    console.log(
      '[SessionManager] Stripping stale auth callback params (valid session exists)'
    );
    currentUrl.searchParams.delete('code');
    currentUrl.searchParams.delete('state');
    window.history.replaceState(
      null,
      '',
      currentUrl.pathname + currentUrl.search + currentUrl.hash
    );
  }

  // Auth0 redirect callback - handles appState.returnTo for deep-link redirects
  // Uses deferred redirect pattern: stores the target in POST_LOGIN_REDIRECT
  // and lets RootLayoutInner navigate AFTER the user profile (and permissions)
  // are fetched. Without this, route permission guards (e.g., playbookPermission)
  // block the deep-link because the permission cache is still empty.
  const onAuth0RedirectCallback = (appState) => {
    // Primary source: appState preserved through Auth0's OAuth state parameter
    // Fallback: sessionStorage set by Login page before loginWithRedirect (survives
    // cases where Auth0 SDK loses the transaction, e.g., tab duplication, SSO auto-auth)
    let returnTo = appState?.returnTo;
    if (!returnTo || returnTo === window.location.pathname) {
      const stored = sessionStorage.getItem(STORAGE_KEYS.AUTH0_REDIRECT);
      if (stored) {
        sessionStorage.removeItem(STORAGE_KEYS.AUTH0_REDIRECT);
        returnTo = stored;
      }
    } else {
      // Clean up sessionStorage if appState worked
      sessionStorage.removeItem(STORAGE_KEYS.AUTH0_REDIRECT);
    }

    if (returnTo && returnTo !== window.location.pathname) {
      console.log(
        '[SessionManager] Auth0 redirect callback, deferring navigation to:',
        returnTo
      );
      // Defer the redirect until RootLayoutInner has fetched the user profile
      // and cached permissions. This prevents route permission guards from
      // blocking the deep-link with a redirect to /bbweb/home.
      storePostLoginRedirect(returnTo);
    }

    // Clean up callback URL (remove code/state params) — same as OIDC flow
    window.history.replaceState(null, '', window.location.pathname);
  };

  return (
    <SessionContext.Provider value={sessionContextValue}>
      {authProvider === 'auth0' ? (
        <Auth0Provider {...authConfig} onRedirectCallback={onAuth0RedirectCallback}>
          {/* Use Auth0-specific wrapper with consolidated hook */}
          <Auth0SessionWrapper loginRoute={loginRoute} nonce={nonce}>{children}</Auth0SessionWrapper>
        </Auth0Provider>
      ) : authProvider === 'gcp' ? (
        /* GCP uses Firebase — no outer OIDC/Auth0 provider needed */
        <GCPSessionWrapper loginRoute={loginRoute} nonce={nonce}>{children}</GCPSessionWrapper>
      ) : (
        <AuthProvider {...authConfig}>
          {/* Use OIDC wrapper with consolidated hook (Web Locks leader election) */}
          <OIDCSessionWrapper loginRoute={loginRoute} nonce={nonce}>{children}</OIDCSessionWrapper>
        </AuthProvider>
      )}
    </SessionContext.Provider>
  );
};

SessionManager.propTypes = {
  configEndpoint: PropTypes.string,
  homeRoute: PropTypes.string,
  loginRoute: PropTypes.string,
  isSatellite: PropTypes.bool,
  /** CSP nonce for runtime-injected <style> tags (e.g. timeout dialog keyframes) */
  nonce: PropTypes.string,
  children: PropTypes.node.isRequired,
  LoadingComponent: PropTypes.elementType,
  ErrorComponent: PropTypes.elementType,
};

export { LoadingScreen, ErrorScreen };
export default SessionManager;
